import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { LineChart } from "@dive.tech/ui-graphs";
import LineChartTooltip from "@dive.tech/ui-graphs/Tooltips/LineChartTooltip";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  FormControlLabel,
  FormGroup,
  Paper,
  Switch,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useClientModelVariables, useModelResponseCurves } from "@services/api";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ModelResponseCurvesProps {
  breakdownChannel: boolean;
  handleBreakdownChannelChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}
const ModelResponseCurves = ({
  breakdownChannel,
  handleBreakdownChannelChange
}: ModelResponseCurvesProps) => {
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const mdValue = theme.breakpoints.values.md; // 900

  const { data: modelMediaVars, isFetching: isLoadingModelVars } =
    useClientModelVariables(currentClient.model.id, "media");

  const { data, isFetching: isLoadingData } = useModelResponseCurves(
    currentClient.model.id
  );

  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(1000);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const chartData = useMemo(() => {
    const mediaColor: { [key: string]: string } = {};
    (modelMediaVars || []).forEach((modelVar) => {
      mediaColor[modelVar.name] = modelVar.color;
    });
    return (data?.media || []).map((media) => {
      return {
        id: media.name,
        color: mediaColor[media.name] ?? "#0BA5EC",
        data: media.points.map((point) => {
          return {
            x: parseInt(parseFloat(point.x).toFixed(0)),
            y: point.y
          };
        })
      };
    });
  }, [data, modelMediaVars]);

  const hiddenSeries = useMemo(() => {
    const mediaData = data?.media || [];

    const mediaWidth = mediaData.map((media) => {
      const minXMedia = Math.min(
        ...media.points.map((point) => parseFloat(point.x))
      );
      const maxXMedia = Math.max(
        ...media.points.map((point) => parseFloat(point.x))
      );

      return {
        name: media.name,
        width: maxXMedia - minXMedia,
        minX: minXMedia
      };
    });
    const avgWidth =
      mediaWidth
        .filter((media) => media.width > 0)
        .reduce((acc, b) => acc + b.width, 0) /
      (mediaWidth.filter((media) => media.width > 0).length || 1);

    return mediaWidth
      .filter((media) => media.minX + media.width >= avgWidth)
      .map((media) => media.name);
  }, [data]);

  const isLoading = useMemo(
    () => isLoadingModelVars || isLoadingData,
    [isLoadingModelVars, isLoadingData]
  );

  return (
    <Box className="w-full pt-1 pl-4 pr-4" sx={{ height: "100%" }}>
      <Paper elevation={1} sx={{ height: "100%" }} ref={chartContainerRef}>
        {isLoading ? (
          <TitledChartSkeleton hasButtons />
        ) : (
          <Box className="w-full" sx={{ height: "100%" }}>
            <Box className="w-full flex pt-4 pl-6">
              <Typography variant="h6" component="div">
                {t("mmmodel.response_curves.title")}
              </Typography>
              <FormGroup className="pl-8">
                <FormControlLabel
                  control={
                    <Switch
                      checked={breakdownChannel}
                      onChange={handleBreakdownChannelChange}
                      color="info"
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={t("mmmodel.response_curves.switch")}
                />
              </FormGroup>
            </Box>

            <Box
              sx={{
                overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
                scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
                "&::-webkit-scrollbar": {
                  display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
                },
                height: "100%"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: isMobile ? mdValue : chartWidth,
                  display: "flex"
                }}
              >
                <LineChart
                  data={chartData}
                  responsiveLineProps={{
                    tooltip: (props: any) =>
                      LineChartTooltip({
                        point: props.point,
                        xAxis: { xType: "number", xDecimals: 0 },
                        yAxis: { yType: "number", yDecimals: 0 }
                      }),
                    theme: chartTheme,
                    width: isMobile ? mdValue : chartWidth,
                    height: isMobile ? 450 : 500,
                    animate: true,
                    useMesh: true,
                    initialHiddenIds: hiddenSeries,
                    yScale: {
                      type: "linear",
                      min: "auto"
                    },
                    xScale: {
                      type: "linear",
                      min: "auto"
                    },
                    axisLeft: {
                      tickSize: 10,
                      tickRotation: 0,
                      legend: currentClient.client.kpi_label,
                      legendOffset: -70,
                      legendPosition: "middle"
                    },
                    axisBottom: {
                      tickRotation: isTablet ? -25 : 0,
                      legend: t("mmmodel.response_curves.media_spend_legend", {
                        currency: currentClient.client.currency
                      }),
                      legendOffset: 55,
                      legendPosition: "middle"
                    },
                    enableGridX: false,
                    enableGridY: false,
                    curve: "linear",
                    margin: {
                      top: 20,
                      left: 90,
                      right: 200,
                      bottom: 140
                    },
                    legends: [
                      {
                        anchor: "right",
                        direction: "column",
                        itemHeight: 10,
                        itemWidth: 120,
                        itemDirection: "left-to-right",
                        symbolSize: 15,
                        itemsSpacing: 12,
                        toggleSerie: true,
                        translateX: 150,
                        translateY: 0,
                        itemOpacity: 1,
                        symbolShape: (s) => (
                          <line
                            x1={s.x}
                            y1={s.y + 7} // Coloca la línea a distancia del y inicial para centrarla verticalmente
                            x2={s.x + 15} // Largo de la línea
                            y2={s.y + 7} // Mantener y1 e y2 iguales para que sea horizontal
                            stroke={s.fill} // El color de la línea es el mismo que el 'fill'
                            strokeWidth={4} // Grosor de la línea
                            strokeLinecap="round" // Hace los extremos de la línea redondeados
                          />
                        )
                      }
                    ]
                  }}
                />
                {isMobile ? (
                  <SwipeIcon
                    sx={{
                      bottom: 12,
                      opacity: 0.4,
                      left: "50%",
                      transform: "translate(750%, 0%)",
                      color: theme.palette.primary.main
                    }}
                    fontSize="small"
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ModelResponseCurves;
